/**
 * Icon Preloader - Optimizes loading of critical navigation icons
 * Implements preloading strategies for better performance
 */

// Critical navigation icons that should be preloaded immediately
const CRITICAL_ICONS = [
  '/icons/home.png',
  '/icons/journal.png', 
  '/icons/breathe.png',
  '/icons/memorial.png',
  '/icons/pet-paw.png'
];

// Non-critical icons that can be lazy loaded
const NON_CRITICAL_ICONS = [
  '/icons/closeicon.png',
  '/icons/settingicon.png',
  '/icons/loadingicon.png',
  '/icons/successicon.png',
  '/icons/uploadicon.png',
  // Emotional state icons
  '/icons/hearticon.png',
  '/icons/dropicon.png',
  '/icons/cloudicon.png',
  '/icons/staricon.png',
  '/icons/sprouticon.png'
];

class IconPreloader {
  constructor() {
    this.preloadedIcons = new Set();
    this.lazyLoadedIcons = new Set();
    this.loadingPromises = new Map();
    this.intersectionObserver = null;
    this.initializeIntersectionObserver();
  }

  /**
   * Initialize intersection observer for lazy loading
   */
  initializeIntersectionObserver() {
    if (typeof window === 'undefined' || !('IntersectionObserver' in window)) {
      return;
    }

    this.intersectionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const iconSrc = entry.target.dataset.iconSrc;
            if (iconSrc && !this.lazyLoadedIcons.has(iconSrc)) {
              this.loadIcon(iconSrc);
              this.intersectionObserver.unobserve(entry.target);
            }
          }
        });
      },
      {
        rootMargin: '50px', // Start loading 50px before the icon comes into view
        threshold: 0.1
      }
    );
  }

  /**
   * Preload critical navigation icons immediately
   * @returns {Promise<void>}
   */
  async preloadCriticalIcons() {
    const preloadPromises = CRITICAL_ICONS.map(iconSrc => this.loadIcon(iconSrc));
    
    try {
      await Promise.all(preloadPromises);
      console.log('Critical icons preloaded successfully');
    } catch (error) {
      console.warn('Some critical icons failed to preload:', error);
    }
  }

  /**
   * Load a single icon with caching
   * @param {string} iconSrc - The icon source URL
   * @returns {Promise<HTMLImageElement>}
   */
  loadIcon(iconSrc) {
    // Return existing promise if already loading
    if (this.loadingPromises.has(iconSrc)) {
      return this.loadingPromises.get(iconSrc);
    }

    // Return resolved promise if already loaded
    if (this.preloadedIcons.has(iconSrc) || this.lazyLoadedIcons.has(iconSrc)) {
      return Promise.resolve();
    }

    const loadPromise = new Promise((resolve, reject) => {
      const img = new Image();
      
      img.onload = () => {
        this.preloadedIcons.add(iconSrc);
        this.loadingPromises.delete(iconSrc);
        resolve(img);
      };
      
      img.onerror = () => {
        this.loadingPromises.delete(iconSrc);
        reject(new Error(`Failed to load icon: ${iconSrc}`));
      };
      
      img.src = iconSrc;
    });

    this.loadingPromises.set(iconSrc, loadPromise);
    return loadPromise;
  }

  /**
   * Setup lazy loading for an icon element
   * @param {HTMLElement} element - The element to observe
   * @param {string} iconSrc - The icon source URL
   */
  setupLazyLoading(element, iconSrc) {
    if (!this.intersectionObserver || this.preloadedIcons.has(iconSrc)) {
      return;
    }

    element.dataset.iconSrc = iconSrc;
    this.intersectionObserver.observe(element);
  }

  /**
   * Preload icons based on user interaction patterns
   * @param {string[]} iconSources - Array of icon sources to preload
   */
  preloadOnInteraction(iconSources) {
    const preloadOnFirstInteraction = () => {
      iconSources.forEach(iconSrc => {
        if (!this.preloadedIcons.has(iconSrc)) {
          this.loadIcon(iconSrc);
        }
      });
      
      // Remove all event listeners after first interaction
      this.removeInteractionListeners(preloadOnFirstInteraction);
    };

    // Add comprehensive interaction listeners for all device types
    const events = [
      'touchstart',    // Mobile primary
      'touchmove',     // Mobile scroll
      'click',         // Universal
      'keydown',       // Keyboard navigation
      'mouseenter',    // Desktop (if available)
      'mouseover',     // Desktop fallback
      'focus',         // Screen readers & keyboard
      'pointerdown'    // Pointer events (unified)
    ];

    events.forEach(event => {
      document.addEventListener(event, preloadOnFirstInteraction, { 
        once: true, 
        passive: true 
      });
    });
  }

  /**
   * Remove all interaction event listeners
   * @param {Function} callback - The callback function to remove
   */
  removeInteractionListeners(callback) {
    const events = [
      'touchstart', 'touchmove', 'click', 'keydown', 
      'mouseenter', 'mouseover', 'focus', 'pointerdown'
    ];
    
    events.forEach(event => {
      document.removeEventListener(event, callback);
    });
  }

  /**
   * Get loading status for an icon
   * @param {string} iconSrc - The icon source URL
   * @returns {string} - 'loaded', 'loading', or 'not-loaded'
   */
  getLoadingStatus(iconSrc) {
    if (this.preloadedIcons.has(iconSrc) || this.lazyLoadedIcons.has(iconSrc)) {
      return 'loaded';
    }
    if (this.loadingPromises.has(iconSrc)) {
      return 'loading';
    }
    return 'not-loaded';
  }

  /**
   * Clear all cached icons and observers
   */
  cleanup() {
    this.preloadedIcons.clear();
    this.lazyLoadedIcons.clear();
    this.loadingPromises.clear();
    
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
    }
  }

  /**
   * Get performance metrics
   * @returns {object} - Performance metrics
   */
  getMetrics() {
    return {
      preloadedCount: this.preloadedIcons.size,
      lazyLoadedCount: this.lazyLoadedIcons.size,
      currentlyLoading: this.loadingPromises.size,
      totalCached: this.preloadedIcons.size + this.lazyLoadedIcons.size
    };
  }
}

// Create singleton instance
const iconPreloader = new IconPreloader();

// Auto-preload critical icons when module loads
if (typeof window !== 'undefined') {
  // Use requestIdleCallback if available, otherwise setTimeout
  const schedulePreload = () => {
    iconPreloader.preloadCriticalIcons();
  };

  if ('requestIdleCallback' in window) {
    requestIdleCallback(schedulePreload);
  } else {
    setTimeout(schedulePreload, 100);
  }
}

export default iconPreloader;

// Export utilities for external use
export {
  CRITICAL_ICONS,
  NON_CRITICAL_ICONS
};